import { memo, useCallback, useMemo } from 'react';
import { IconBounceRight } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updateCaptionStyles } from '@/stores/clip';
import { CaptionLineConfig } from '@/domains/asset';
import { TransitionInEffect } from '@/App/remotion/libs/transitions/types';

enum AnimationMode {
  PerWord = 'Per Word',
  PerSlide = 'Per Slide'
}

const MODE_OPTIONS = [
  { label: 'Per Word', value: AnimationMode.PerWord },
  { label: 'Per Slide', value: AnimationMode.PerSlide }
];

const EFFECT_OPTIONS = {
  [AnimationMode.PerWord]: [
    { label: 'None', value: TransitionInEffect.None },
    { label: 'Fade In', value: TransitionInEffect.Fade },
    { label: 'Slide Up', value: TransitionInEffect.SlideUp },
    { label: 'Spring', value: TransitionInEffect.Spring },
    { label: 'Appear', value: TransitionInEffect.Appear }
  ],
  [AnimationMode.PerSlide]: [
    { label: 'None', value: TransitionInEffect.None },
    { label: 'Fade In', value: TransitionInEffect.Fade },
    { label: 'Slide Up', value: TransitionInEffect.SlideUp },
    { label: 'Spring', value: TransitionInEffect.Spring }
  ]
};

function CaptionAnimationPopover() {
  const { clipId, clipData } = useClipsContext();

  const animationMode = useMemo(
    () =>
      clipData.asset_metadata.caption_styles?.transitions?.wordIn ? AnimationMode.PerWord : AnimationMode.PerSlide,
    [clipData.asset_metadata.caption_styles?.transitions?.wordIn]
  );

  const animationEffect = useMemo(() => {
    if (animationMode === AnimationMode.PerWord) {
      return clipData.asset_metadata.caption_styles?.transitions?.wordIn;
    }

    if (animationMode === AnimationMode.PerSlide) {
      return clipData.asset_metadata.caption_styles?.transitions?.pageIn;
    }

    return TransitionInEffect.None;
  }, [
    animationMode,
    clipData.asset_metadata.caption_styles?.transitions?.pageIn,
    clipData.asset_metadata.caption_styles?.transitions?.wordIn
  ]);

  const onAnimationModeChange = useCallback(
    (mode: AnimationMode) => {
      updateCaptionStyles(clipId, {
        transitions: {
          pageIn: mode === AnimationMode.PerSlide ? animationEffect : undefined,
          wordIn: mode === AnimationMode.PerWord ? animationEffect : undefined
        }
      });
    },
    [clipId, animationEffect]
  );

  const onAnimationEffectChange = useCallback(
    (newEffect: TransitionInEffect) => {
      updateCaptionStyles(clipId, {
        transitions: {
          pageIn: animationMode === AnimationMode.PerSlide ? newEffect : undefined,
          wordIn: animationMode === AnimationMode.PerWord ? newEffect : undefined
        }
      });
    },
    [clipId, animationMode]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-animation-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconBounceRight size={16} />
              <PlayerMenuTooltip content="Animation Mode" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                <h3 className="text-sm font-medium text-slate-500">Animation Settings</h3>

                {clipData?.asset_metadata?.caption_styles?.line_config !== CaptionLineConfig.OneWord && (
                  <div className="grid grid-cols-12 items-center justify-between gap-2">
                    <span className="col-span-3 text-sm font-medium">Mode</span>
                    <span className="col-span-9 flex items-center">
                      <select
                        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                        value={animationMode}
                        onChange={e => onAnimationModeChange(e.target.value as AnimationMode)}
                      >
                        {MODE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Effect</span>
                  <span className="col-span-9 flex items-center">
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      value={animationEffect}
                      onChange={e => onAnimationEffectChange(e.target.value as TransitionInEffect)}
                    >
                      {EFFECT_OPTIONS[animationMode].map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionAnimationPopover);
